import React, { useCallback, useEffect } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { replace } from 'connected-react-router';
import { makeStyles, Grid } from '@material-ui/core';
import isEmpty from 'lodash/isEmpty';

import StandardLayout from 'components/Layouts/Standard';
import {
  getSettingsClean,
  getSettingsRequest,
  validateChannelRequest,
  selectSettingsIsLoading,
  selectSettingsError,
  selectChannelEmail,
  selectValidateChannelValidationError,
  validateChannelClean,
} from 'store/settings';
import { emailBlacklistValidator } from 'utils/formControls';
import useQuery from 'hooks/useQuery';
import { ChannelType } from 'types/settings';
import { ROUTES } from 'constants/routes';

import { EMAIL_PATTERN } from '#shared/constants/formControls';
import Input from '#web-components/components/FormControls/Input';
import Button, { ButtonVariants } from '#web-components/components/Button';
import FlashMessage from '#web-components/components/FlashMessage';

import styles from './ProfileEditPage.styles';

const useStyles = makeStyles(styles, { name: 'ProfilePage' });

export default function ProfileEditPage() {
  const { state: locationState = {} } = useLocation<{ isEditEmail?: boolean }>();

  const { t } = useTranslation('pages');
  const classes = useStyles();
  const dispatch = useDispatch();
  const settings = useSelector(selectChannelEmail);
  const isLoading = useSelector(selectSettingsIsLoading);
  const error = useSelector(selectSettingsError);
  const serverValidateErrorMessage = useSelector(selectValidateChannelValidationError);
  const addressQuery = useQuery().get('address');

  const methods = useForm({
    shouldUnregister: false,
    mode: 'onChange',
    defaultValues: {
      email: addressQuery || settings?.address || '',
    },
  });
  const {
    control, setValue, getValues, setError,
  } = methods;

  useEffect(() => {
    dispatch(getSettingsRequest());
    return () => {
      dispatch(getSettingsClean());
    };
  }, [dispatch, setError]);

  useEffect(() => {
    if (settings) {
      const { address } = settings;
      setValue('email', addressQuery || address || '');
    }
  }, [settings, setValue, addressQuery]);

  useEffect(() => {
    if (serverValidateErrorMessage) {
      setError('email', { message: serverValidateErrorMessage });
    }

    return () => {
      dispatch(validateChannelClean());
    };
  }, [dispatch, serverValidateErrorMessage, setError]);

  const onSubmit = useCallback(() => {
    const data = getValues();
    dispatch(replace(`${ROUTES.PROFILE_EDIT}?address=${data.email}`));
    dispatch(validateChannelRequest({
      channel: ChannelType.EMAIL,
      address: data.email,
    }));
  }, [dispatch, getValues]);

  const handleSubmit = useCallback(() => {
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  const emailValidator = (email: string) => {
    if (emailBlacklistValidator(email)) {
      return t('errors:validation.emailBlacklist');
    }

    if (locationState.isEditEmail && settings?.address === email) {
      return t('profileEdit.validation.emailMatchesCurrentOne');
    }

    return true;
  };

  return (
    <StandardLayout
      backLink={{
        title: t('profileEdit.backToProfile'),
        path: ROUTES.PROFILE,
        RouterNavLink,
      }}
      title={locationState.isEditEmail ? t('profileEdit.editEmailTitle') : t('profileEdit.addEmailTitle')}
      isLoading={isLoading}
      error={error}
    >
      <form className={classes.root}>
        <Grid container>
          <Grid item sm={8}>
            <div className={classes.message}>
              <FlashMessage
                status="info"
                title={t('profileEdit.sidebarTitle')}
                message={t('profileEdit.sidebarDescription')}
              />
            </div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('errors:validation.required') as string,
                pattern: { value: EMAIL_PATTERN, message: t('profileEdit.validation.emailPattern') },
                validate: emailValidator,
              }}
              render={({ field, fieldState }) => (
                <Input
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  label={t('profileEdit.fields.email.label')}
                  error={fieldState.error}
                  placeholder={t('profileEdit.fields.email.placeholder')}
                  description={t('profileEdit.fields.email.hint')}
                  isLabelShrink
                  fullWidth
                  inputProps={{
                    'data-xpath': 'profile.edit.fields.email',
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <div className={classes.buttonContainer}>
          <Button
            variant={ButtonVariants.primary}
            onClick={handleSubmit}
            disabled={!isEmpty(methods.formState.errors)}
            size="large"
            data-xpath="profile.edit.submitButton"
          >
            {t('profileEdit.submitButton')}
          </Button>
        </div>
      </form>
    </StandardLayout>
  );
}
